import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';

/* Your storage: how much you're using, and more storage paid in USDC.
   Payment goes straight to B/N's wallet; B/N sees it on-chain and adds the
   storage automatically — or you paste the transaction ID. */

const MB = 1024 * 1024;
const fmt = (b) => (b >= 1024 * MB ? `${(b / (1024 * MB)).toFixed(b % (1024 * MB) ? 1 : 0)} GB` : `${(b / MB).toFixed(1)} MB`);
const day = (iso) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

function Copy({ text, label, notify }) {
  return (
    <button type="button" className="btn quiet sm" onClick={() => {
      navigator.clipboard?.writeText(text).then(() => notify?.(`${label} copied`)).catch(() => window.prompt(`Copy the ${label}:`, text));
    }}>Copy</button>
  );
}

function PayScreen({ order, notify, onDone, onCancel }) {
  const [o, setO] = useState(order);
  const [net, setNet] = useState(order.pay.networks.find((n) => n.recommended)?.key || order.pay.networks[0].key);
  const [tx, setTx] = useState('');
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  const timer = useRef(null);

  const check = useCallback(async (explicit) => {
    try {
      const r = await api.checkOrder(o.id);
      setO((prev) => ({ ...prev, ...r, pay: r.pay || prev.pay }));
      if (r.status === 'paid') { notify?.('Payment received — your storage is added'); onDone(); }
      else if (explicit) notify?.("Not seen yet — it can take a minute or two after you send");
    } catch (e) { if (explicit) notify?.(e.message); }
  }, [o.id, notify, onDone]);

  useEffect(() => {
    timer.current = setInterval(() => { setNow(Date.now()); }, 1000);
    const poll = setInterval(() => check(false), 20000);
    return () => { clearInterval(timer.current); clearInterval(poll); };
  }, [check]);

  const left = Math.max(0, Math.floor((new Date(o.pay.expires_at).getTime() - now) / 1000));
  async function claim() {
    setBusy(true);
    try {
      const r = await api.claimOrder(o.id, tx.trim(), net);
      if (r.status === 'paid') { notify?.('Payment confirmed — your storage is added'); onDone(); }
    } catch (e) { notify?.(e.message); } finally { setBusy(false); }
  }

  return (
    <div className="card pad pay">
      <b>{o.label}</b>
      <ol className="pay-steps">
        <li>
          Choose the network:
          <div className="mp-chips">
            {o.pay.networks.map((n) => (
              <button key={n.key} type="button" className={`mp-chip${net === n.key ? ' on' : ''}`} onClick={() => setNet(n.key)}>
                {n.label}{n.recommended ? ' (recommended)' : ''}
              </button>
            ))}
          </div>
          {net === 'ethereum' && <p className="muted-sm warn">Ethereum network fees are often $1–10 — Base costs under a cent.</p>}
        </li>
        <li>
          Send <b>exactly</b> this much <b>USDC</b> on <b>{o.pay.networks.find((n) => n.key === net)?.label}</b>:
          <div className="pay-line"><code className="pay-amount">{o.pay.amount_usdc} USDC</code><Copy text={o.pay.amount_usdc} label="amount" notify={notify} /></div>
          <p className="muted-sm">The odd cents are how we recognise your payment — please send the exact amount.</p>
        </li>
        <li>
          To B/N's wallet:
          <div className="pay-line"><code className="pay-addr">{o.pay.address}</code><Copy text={o.pay.address} label="address" notify={notify} /></div>
        </li>
      </ol>
      <p className="muted-sm warn">Only USDC, only on {o.pay.networks.map((n) => n.label).join(' or ')}. Anything else can't be matched and may be lost.</p>
      <p className="muted-sm">
        {left > 0 ? `This amount is held for you for ${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}. ` : 'This amount has timed out — a payment sent in the last day still counts. '}
        We check the network automatically; it usually takes a minute or two after you send.
      </p>
      <div className="mp-actions">
        <button className="btn primary sm" onClick={() => check(true)}>I've sent it — check now</button>
        <button className="btn quiet sm" onClick={onCancel}>Cancel</button>
      </div>
      <details className="pay-claim">
        <summary className="muted-sm">Sent it but it isn't showing? Paste your transaction ID</summary>
        <div className="invite-row">
          <input className="in" value={tx} placeholder="0x… (from your wallet)" onChange={(e) => setTx(e.target.value)} />
          <button className="btn ghost sm" disabled={busy || tx.trim().length < 60} onClick={claim}>{busy ? 'Checking…' : 'Confirm'}</button>
        </div>
      </details>
    </div>
  );
}

export default function StoragePanel({ notify, refreshKey, onChanged }) {
  const [s, setS] = useState(null);
  const [choosing, setChoosing] = useState(false);
  const [order, setOrder] = useState(null);
  const [term, setTerm] = useState('12m');
  const load = useCallback(() => api.myStorage().then((d) => {
    setS(d);
    const pending = d.orders.find((x) => x.status === 'pending' && x.pay);
    if (pending) setOrder((cur) => cur || pending);
  }).catch(() => {}), []);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!s) return null;

  const pct = Math.min(100, Math.round((s.used_bytes / s.quota_bytes) * 100));
  const packs = s.products.filter((p) => p.kind === 'storage' && p.key.endsWith(term));
  const start = async (key) => {
    try { setOrder(await api.createOrder(key)); setChoosing(false); } catch (e) { notify?.(e.message); }
  };

  return (
    <div className="storage">
      <div className="storage-head">
        <span>Storage: <b>{fmt(s.used_bytes)}</b> of {fmt(s.quota_bytes)}</span>
        {s.payments_open && !order && <button className="linklike" onClick={() => setChoosing(!choosing)}>{choosing ? 'Close' : 'Get more storage'}</button>}
      </div>
      <div className="storage-bar" aria-hidden="true"><span style={{ width: `${pct}%` }} className={pct > 85 ? 'high' : ''} /></div>
      {s.grants.length > 0 && (
        <p className="muted-sm">{s.grants.map((g) => `+${fmt(g.bytes)} until ${day(g.ends_at)}`).join(' · ')} — free allowance {fmt(s.free_bytes)}</p>
      )}

      {choosing && (
        <div className="card pad">
          <div className="mp-chips">
            <button type="button" className={`mp-chip${term === '3m' ? ' on' : ''}`} onClick={() => setTerm('3m')}>3 months</button>
            <button type="button" className={`mp-chip${term === '12m' ? ' on' : ''}`} onClick={() => setTerm('12m')}>12 months (2 free)</button>
          </div>
          <div className="pack-grid">
            {packs.map((p) => (
              <button key={p.key} type="button" className="pack" onClick={() => start(p.key)}>
                <b>{p.label.split(' · ')[0]}</b>
                <span>+{fmt(p.bytes)}</span>
                <em>${p.price_usd} USDC</em>
              </button>
            ))}
          </div>
          <p className="muted-sm">Paid in USDC on Base (fees under a cent) or Ethereum. Nothing is ever deleted when a pack ends — you just can't add more until you're under the free {fmt(s.free_bytes)}.</p>
        </div>
      )}

      {order && <PayScreen order={order} notify={notify}
        onCancel={() => api.cancelOrder(order.id).catch(() => {}).finally(() => { setOrder(null); load(); })}
        onDone={() => { setOrder(null); load(); onChanged?.(); }} />}

      {s.orders.filter((x) => x.status === 'paid').length > 0 && (
        <details className="pay-history">
          <summary className="muted-sm">Payment history</summary>
          {s.orders.filter((x) => x.status === 'paid').map((x) => (
            <p key={x.id} className="muted-sm">{day(x.paid_at)} · {x.label} · ${x.price_usd} · {x.network} · <code>{x.tx_hash.slice(0, 10)}…</code></p>
          ))}
        </details>
      )}
    </div>
  );
}
